import React, { useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from './auth/authConfig';
import './PaywallModal.css';
import './AnonymousSignUpModal.css';

/**
 * Modal shown to anonymous visitors who have either just used their last
 * free blog or attempted to generate one past the free limit.
 *
 * Copy leans on the incentive promised by the free-trial feature: they've
 * already tasted the product; signing up gives them N more free blogs plus
 * the standard trial credits so there's no reason to bail.
 *
 * Props:
 *   onClose    – optional dismiss handler. When omitted, the close button and
 *                secondary CTA are hidden. Pass a handler so users can
 *                dismiss (e.g. to copy the last free blog) while signup stays
 *                available from the app until they are ready to generate again.
 *   blogsUsed  – how many anonymous blogs they've already generated (0..2)
 *   freeLimit  – server-reported free anonymous limit (defaults to 2)
 *   bonusBlogs – how many additional blogs signup grants (defaults to 4)
 *   reason     – 'limit-reached' (hard block) | 'incentive' (soft prompt)
 */
export default function AnonymousSignUpModal({
  onClose,
  blogsUsed = 2,
  freeLimit = 2,
  bonusBlogs = 4,
  reason = 'limit-reached',
}) {
  const { instance } = useMsal();
  const [busy, setBusy] = useState(false);

  const isSoftPrompt = reason === 'incentive';
  const canDismiss = typeof onClose === 'function';

  const handleSignUp = async () => {
    setBusy(true);
    try {
      await instance.loginRedirect(loginRequest);
    } catch (err) {
      console.error('Sign-up redirect failed:', err);
      setBusy(false);
    }
  };

  const title = isSoftPrompt
    ? `You've got ${Math.max(0, freeLimit - blogsUsed)} free blog${(freeLimit - blogsUsed) === 1 ? '' : 's'} left`
    : "You've used your free blogs";

  const subtitle = isSoftPrompt
    ? `Sign up now and we'll add ${bonusBlogs} more free blogs to your account — plus the standard welcome credits.`
    : `Create a free account to get ${bonusBlogs} more free blogs and keep generating. No credit card required.`;

  return (
    <div className="paywall-overlay" role="dialog" aria-modal="true" aria-label="Create an account">
      <div className="paywall-modal anon-signup-modal">
        {canDismiss && (
          <button
            className="paywall-modal__close"
            onClick={onClose}
            aria-label="Close"
          >
            ✕
          </button>
        )}

        <div className="paywall-modal__icon">✦</div>

        <h2 className="paywall-modal__title">{title}</h2>
        <p className="paywall-modal__subtitle">{subtitle}</p>

        <div className="paywall-modal__pack anon-signup-modal__pack">
          <div className="paywall-modal__pack-header">
            <span className="paywall-modal__pack-label">Sign up free</span>
          </div>
          <div className="anon-signup-modal__headline">
            + {bonusBlogs} free blogs
          </div>
          <ul className="paywall-modal__pack-features">
            <li>✓ {bonusBlogs} additional free blogs on us</li>
            <li>✓ Save and revisit your blog drafts</li>
            <li>✓ Brand voice &amp; style settings</li>
            <li>✓ No credit card required</li>
          </ul>
          <button
            className="paywall-modal__buy-btn"
            onClick={handleSignUp}
            disabled={busy}
          >
            {busy ? 'Opening sign-up…' : 'Create free account'}
          </button>
        </div>

        {canDismiss && (
          <button
            type="button"
            className="anon-signup-modal__dismiss"
            onClick={onClose}
            disabled={busy}
          >
            {isSoftPrompt
              ? 'Maybe later — use my last free blog'
              : 'Not now — I want to copy and edit my blog first'}
          </button>
        )}

        <p className="paywall-modal__note">
          Already have an account? Clicking sign up will also log you in.
        </p>
      </div>
    </div>
  );
}
